import axios from 'axios';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost/api';

// Get auth token from localStorage or URL params
const getAuthToken = () => {
    // Check URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('auth_token');

    if (tokenFromUrl) {
        localStorage.setItem('auth_token', tokenFromUrl);
        return tokenFromUrl;
    }

    // Fall back to localStorage
    return localStorage.getItem('auth_token');
};

// Create axios instance with default config
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

/**
 * Fetch all products with complete details including addons, pricing, and colors
 * @returns {Promise<Array>} Array of products with full details
 */
export const fetchProductsWithDetails = async () => {
    try {
        const response = await apiClient.get('/products/details');

        if (response.data.success) {
            return response.data.data;
        } else {
            throw new Error('Failed to fetch products');
        }
    } catch (error) {
        console.error('Error fetching products:', error);
        throw error;
    }
};

/**
 * Get a specific user product by ID (public, no authentication required).
 * Fetches the vendor's user product which contains pricing_data and addons.
 * @param {number} productId - The user product ID
 * @returns {Promise<Object>} User product details with pricing_data and addons
 */
export const fetchProductById = async (productId) => {
    try {
        // Use public user-product endpoint — returns pricing_data + addons set by the vendor
        const response = await axios.get(`${API_BASE_URL}/public/user-products/${productId}`, {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        });

        if (response.data.success) {
            return response.data.data;
        } else {
            throw new Error(`User product with ID ${productId} not found`);
        }
    } catch (error) {
        console.error('Error fetching user product by ID:', error);
        throw error;
    }
};

/**
 * Get price from pricing matrix based on width and depth
 * @param {Object} pricingData - The pricing data object with widths, depths, and prices
 * @param {number} width - The width value
 * @param {number} depth - The depth value
 * @returns {number} The price for the given dimensions
 */
export const getPriceFromMatrix = (pricingData, width, depth) => {
    if (!pricingData || !pricingData.widths || !pricingData.prices) {
        console.warn('Invalid pricing data structure');
        return 0;
    }

    // Support both 'depths' (legacy) and 'heights' (new CRM format)
    const depthValues = pricingData.depths || pricingData.heights;
    if (!depthValues || depthValues.length === 0) {
        console.warn('No depth/height values in pricing data');
        return 0;
    }

    const { widths, prices } = pricingData;
    const depths = depthValues;

    // Find the closest width index
    let widthIndex = widths.findIndex(w => w >= width);
    if (widthIndex === -1) widthIndex = widths.length - 1;
    if (widthIndex === 0 && width < widths[0]) widthIndex = 0;

    // Find the closest depth index
    let depthIndex = depths.findIndex(d => d >= depth);
    if (depthIndex === -1) depthIndex = depths.length - 1;
    if (depthIndex === 0 && depth < depths[0]) depthIndex = 0;

    // Get the price from the matrix
    if (prices[widthIndex] && prices[widthIndex][depthIndex] !== undefined) {
        return parseFloat(prices[widthIndex][depthIndex]);
    }

    console.warn(`Price not found for width ${width} and depth ${depth}`);
    return 0;
};

/**
 * Filter active addons that have pricing data
 * @param {Array} addons - Array of addon objects
 * @returns {Array} Filtered array of active addons with pricing
 */
export const filterActiveAddons = (addons) => {
    if (!Array.isArray(addons)) {
        return [];
    }

    return addons.filter(addon => {
        // Check if addon has valid pricing data
        const pd = addon.pricing_data;
        if (!pd || !pd.widths || !pd.prices) return false;
        // Support both 'depths' (legacy/API normalised) and 'heights' (CRM editor format)
        const depthValues = pd.depths || pd.heights;
        if (!depthValues || pd.widths.length === 0 || depthValues.length === 0) return false;

        // prices can be a 2D array OR an object {"col-row": price}
        const prices = pd.prices;
        if (Array.isArray(prices)) {
            return prices.length > 0;
        } else if (typeof prices === 'object') {
            return Object.keys(prices).length > 0;
        }
        return false;
    });
};

/**
 * Calculate total price for a product configuration
 * @param {Object} product - The product object
 * @param {number} width - Selected width
 * @param {number} depth - Selected depth
 * @param {Array} selectedAddons - Array of selected addon IDs
 * @returns {Object} Calculation result with base price, addon prices, and total
 */
export const calculateTotalPrice = (product, width, depth, selectedAddons = []) => {
    let basePrice = 0;
    let addonPrices = {};
    let total = 0;

    // Calculate base product price
    if (product.pricing_data) {
        basePrice = getPriceFromMatrix(product.pricing_data, width, depth);
        total += basePrice;
    }

    // Calculate addon prices
    const activeAddons = filterActiveAddons(product.addons || []);

    selectedAddons.forEach(addonId => {
        const addon = activeAddons.find(a => a.id === addonId);
        if (addon && addon.pricing_data) {
            const addonPrice = getPriceFromMatrix(addon.pricing_data, width, depth);
            addonPrices[addon.id] = {
                name: addon.name,
                type: addon.type,
                price: addonPrice
            };
            total += addonPrice;
        }
    });

    return {
        basePrice,
        addonPrices,
        total: parseFloat(total.toFixed(2))
    };
};

/**
 * Get available dimensions from pricing data
 * @param {Object} pricingData - The pricing data object
 * @returns {Object} Object with available widths and depths
 */
export const getAvailableDimensions = (pricingData) => {
    if (!pricingData || !pricingData.widths) {
        return { widths: [], depths: [] };
    }
    // Support both 'depths' (legacy) and 'heights' (new CRM format)
    const depths = pricingData.depths || pricingData.heights || [];
    return {
        widths: pricingData.widths,
        depths
    };
};

export default {
    fetchProductsWithDetails,
    fetchProductById,
    getPriceFromMatrix,
    filterActiveAddons,
    calculateTotalPrice,
    getAvailableDimensions
};
